import { c as _c } from "react/compiler-runtime";
import cx from 'classix';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { FaBullhorn, FaEnvelope } from 'react-icons/fa6';
import { type MessagesCounters } from '@/features/messages/types/message-counters-types';
import { MessagesType } from '@/features/messages/types/message-counters-types';
import { helpdeskDataAtom } from '@/features/user/atoms/helpdesk-data-atom';
import { loggedUserAtom } from '@/features/user/atoms/logged-user-atom';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import { useRouter } from '@/i18n/routing';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import postReportElastic from '@/shared/requests/post-report-elastic';
import { setSessionStorage } from '@/shared/utils/typed-session-storage';
import Button from '@/ui/button/button';
import NotificationsIndicator from '@/ui/notification-indicator/notifications-indicator';
import styles from './header-logined-links.module.scss';
interface HeaderLoginedLinksProps {
  messagesCounters: MessagesCounters | null;
  megaphoneCount: number;
}
export default function HeaderLoginedLinks(props) {
  const $ = _c(45);
  const {
    megaphoneCount,
    messagesCounters
  } = props;
  useRouter();
  const translationsLinks = useTranslations("global.links");
  const loggedUser = useAtomValue(loggedUserAtom);
  const helpdeskData = useAtomValue(helpdeskDataAtom);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getUserBearerCookie();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const userBearerCookie = t0;
  const inboxMessages = messagesCounters?.[MessagesType.Inbox];
  const hasNewMessages = inboxMessages && (inboxMessages.countDirectNew > 0 || inboxMessages.countMegaphoneNew > 0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (userBearerCookie) {
        postReportElastic({
          bearerToken: userBearerCookie,
          report: {
            action_name: "megaphone_page_request",
            details: {
              src: "header"
            }
          },
          userAgent: globalThis.navigator.userAgent
        });
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleMegaphoneClick = t1;
  let t2;
  if ($[2] !== loggedUser) {
    t2 = () => {
      if (loggedUser && !loggedUser.isPaidAccount) {
        setSessionStorage("paywallActionValue", "paywall_from_header_messages_button");
      }
      if (userBearerCookie) {
        postReportElastic({
          bearerToken: userBearerCookie,
          report: {
            action_name: "mailbox_page_request",
            details: {
              src: "header"
            }
          },
          userAgent: globalThis.navigator.userAgent
        });
      }
    };
    $[2] = loggedUser;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleMessagesClick = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cx(styles.headerLoginedLinks_item, styles.headerLoginedLinks_megaphone);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== megaphoneCount) {
    t4 = megaphoneCount > 0 && <div className={cx(styles.headerLoginedLinks_messageIndicator, styles.headerLoginedLinks_messageIndicatorOne)}><NotificationsIndicator color="blue" text={megaphoneCount > 20 ? "20+" : megaphoneCount} /></div>;
    $[5] = megaphoneCount;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== translationsLinks) {
    t5 = translationsLinks("megaphone");
    $[7] = translationsLinks;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <Button element="nativeLink" variant="ghost" size="lg" href={ROUTER_LINKS.megaphone} onClick={handleMegaphoneClick}>{t5}</Button>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <li className={t3}>{t4}{t6}</li>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = hasNewMessages && styles.headerLoginedLinks_messages__hasNewMessage;
  let t9;
  if ($[14] !== t8) {
    t9 = cx(styles.headerLoginedLinks_item, styles.headerLoginedLinks_messages, t8);
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== inboxMessages) {
    t10 = !!inboxMessages && inboxMessages.countDirectNew > 0 && <div className={cx(styles.headerLoginedLinks_messageIndicator, styles.headerLoginedLinks_messageIndicatorRight)}><NotificationsIndicator icon={FaEnvelope} text={inboxMessages.countDirectNew > 999 ? "999+" : inboxMessages.countDirectNew} /></div>;
    $[16] = inboxMessages;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== inboxMessages) {
    t11 = !!inboxMessages && inboxMessages.countMegaphoneNew > 0 && <div className={cx(styles.headerLoginedLinks_messageIndicator, inboxMessages.countDirectNew ? styles.headerLoginedLinks_messageIndicatorLeft : styles.headerLoginedLinks_messageIndicatorRight)}><NotificationsIndicator icon={FaBullhorn} color="blue" text={inboxMessages.countMegaphoneNew > 999 ? "999+" : inboxMessages.countMegaphoneNew} /></div>;
    $[18] = inboxMessages;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = loggedUser?.isPaidAccount ? ROUTER_LINKS.messagesInbox : ROUTER_LINKS.accountPayments;
  let t13;
  if ($[20] !== translationsLinks) {
    t13 = translationsLinks("messages");
    $[20] = translationsLinks;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== handleMessagesClick || $[23] !== t12 || $[24] !== t13) {
    t14 = <Button element="nativeLink" variant="ghost" size="lg" href={t12} onClick={handleMessagesClick}>{t13}</Button>;
    $[22] = handleMessagesClick;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t14 || $[29] !== t9) {
    t15 = <li className={t9}>{t10}{t11}{t14}</li>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t14;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cx(styles.headerLoginedLinks_item, styles.headerLoginedLinks_help);
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== helpdeskData) {
    t17 = !!helpdeskData?.notificationsCountNew && helpdeskData.notificationsCountNew > 0 && <div className={cx(styles.headerLoginedLinks_messageIndicator, styles.headerLoginedLinks_messageIndicatorOne)}><NotificationsIndicator color="orange" text={helpdeskData.notificationsCountNew > 99 ? "99+" : helpdeskData.notificationsCountNew} /></div>;
    $[32] = helpdeskData;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== translationsLinks) {
    t18 = translationsLinks("help");
    $[34] = translationsLinks;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t18) {
    t19 = <Button element="nativeLink" variant="ghost" size="lg" href={ROUTER_LINKS.help}>{t18}</Button>;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t17 || $[39] !== t19) {
    t20 = <li className={t16}>{t17}{t19}</li>;
    $[38] = t17;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t15 || $[42] !== t20 || $[43] !== t7) {
    t21 = <ul className={styles.headerLoginedLinks}>{t7}{t15}{t20}</ul>;
    $[41] = t15;
    $[42] = t20;
    $[43] = t7;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  return t21;
}